import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { type Column, ListTable, type ListState } from '@/components/admin/ListTable';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import {
  connectIntegration,
  disconnectIntegration,
  fetchIntegrations,
  OAUTH_STATUS_LABELS,
  PROVIDER_LABELS,
  syncIntegration,
  type Integration,
} from '@/features/integrations/api';
import { IntegrationSettingsBox } from '@/features/integrations/IntegrationSettingsBox';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

const CONNECTABLE = new Set(['not_connected', 'expired', 'failed']);
const SYNCABLE = new Set(['connected', 'sync_succeeded']);
const DISCONNECTABLE = new Set([
  'connected',
  'sync_succeeded',
  'expired',
  'failed',
  'authorizing',
  'pending_approval',
]);

/**
 * 外部連携（事務局・運営会社のみ / implementation-spec §12 M11 / DECISIONS D-043）。
 * ローカル/デモ環境では実際に外部へリダイレクトしない。画面操作で状態機械を進める。
 */
export function IntegrationsPage(): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['integrations'], queryFn: fetchIntegrations });
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // 接続設定フォームを開いている連携（D-067）
  const [settingsId, setSettingsId] = useState<string | null>(null);

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['integrations'] });
  }

  async function onConnect(integration: Integration): Promise<void> {
    setBusyId(integration.id);
    setError(null);
    try {
      await connectIntegration(integration.id);
      await invalidate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '連携に失敗しました。');
    } finally {
      setBusyId(null);
    }
  }

  async function onDisconnect(integration: Integration): Promise<void> {
    setBusyId(integration.id);
    setError(null);
    try {
      await disconnectIntegration(integration.id);
      await invalidate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '解除に失敗しました。');
    } finally {
      setBusyId(null);
    }
  }

  async function onSync(integration: Integration, succeed: boolean): Promise<void> {
    setBusyId(integration.id);
    setError(null);
    try {
      await syncIntegration(integration.id, succeed);
      await invalidate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '同期に失敗しました。');
    } finally {
      setBusyId(null);
    }
  }

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  const columns: Column<Integration>[] = [
    { key: 'provider', header: '連携先', primary: true, render: (i) => PROVIDER_LABELS[i.provider] },
    { key: 'oauth_status', header: '状態', render: (i) => OAUTH_STATUS_LABELS[i.oauth_status] },
    { key: 'is_enabled', header: '有効', render: (i) => (i.is_enabled ? '有効' : '無効') },
    {
      key: 'last_sync_at',
      header: '最終同期',
      render: (i) => (i.last_sync_at ? formatDateTime(i.last_sync_at) : '—'),
    },
    { key: 'last_error', header: '直近エラー', render: (i) => i.last_error ?? '—' },
    {
      key: 'actions',
      header: '操作',
      render: (i) => (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => setSettingsId(i.id)}
            className="btn-secondary px-2 py-1"
          >
            接続設定
          </button>
          {CONNECTABLE.has(i.oauth_status) && (
            <button
              type="button"
              disabled={busyId === i.id}
              onClick={() => void onConnect(i)}
              className="btn-secondary px-2 py-1 disabled:opacity-50"
            >
              連携する
            </button>
          )}
          {SYNCABLE.has(i.oauth_status) && (
            <>
              <button
                type="button"
                disabled={busyId === i.id}
                onClick={() => void onSync(i, true)}
                className="btn-secondary px-2 py-1 disabled:opacity-50"
              >
                同期する（成功を模擬）
              </button>
              <button
                type="button"
                disabled={busyId === i.id}
                onClick={() => void onSync(i, false)}
                className="rounded-sm border border-wpnotice-error px-2 py-1 text-wpnotice-error hover:bg-wpnotice-error/10 disabled:opacity-50"
              >
                同期する（失敗を模擬）
              </button>
            </>
          )}
          {DISCONNECTABLE.has(i.oauth_status) && (
            <button
              type="button"
              disabled={busyId === i.id}
              onClick={() => void onDisconnect(i)}
              className="btn-secondary px-2 py-1 disabled:opacity-50"
            >
              解除する
            </button>
          )}
        </div>
      ),
    },
  ];

  const settingsIntegration = (query.data ?? []).find((i) => i.id === settingsId) ?? null;

  return (
    <>
      <PageHeader
        title="外部連携"
        description="Google/Slack/Chatwork/メールの接続設定と連携状態を管理します。「接続設定」で入力した値はサーバーの環境変数より優先されます。ローカル/デモ環境では実際の外部通信は行いません。"
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <ListTable<Integration>
        caption="外部連携一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(i) => i.id}
        state={state}
        onRetry={() => void query.refetch()}
        emptyTitle="連携設定はまだありません"
      />

      {/* 一覧の行を押した結果はモーダルで出す（D-094）。一覧の下にインライン展開すると、
          件数が多いとき押しても画面外に反応が見えず「動かない」ように見える
          （実機フィードバック / D-164） */}
      <Modal
        open={settingsIntegration !== null}
        title={
          settingsIntegration ? `${PROVIDER_LABELS[settingsIntegration.provider]} の接続設定` : ''
        }
        onClose={() => setSettingsId(null)}
        wide
      >
        {settingsId && (
          <IntegrationSettingsBox integrationId={settingsId} onClose={() => setSettingsId(null)} />
        )}
      </Modal>
    </>
  );
}
